import type { BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { retry } from "@reduxjs/toolkit/query/react";
import axios, { AxiosError, type AxiosRequestConfig } from "axios";

import memoizedAuth from "@/lib/firebase/MemoizedAuth";
import type { ApiError } from "@/types/api";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import {
    CORRELATION_ID_HEADER,
    generateCorrelationId,
} from "@/utils/logging/correlationId";
import {
    PREVIEW_EMAIL_OVERRIDE_HEADER,
    getStoredPreviewEmailOverride,
    isPreviewEnvironment,
} from "@/utils/previewEmailOverride";

import {
    logOperationAbort,
    logOperationFail,
    logOperationStart,
    logOperationSuccess,
} from "./baseQueryWithAuth.logging";
import {
    normalizeApiError,
    normalizeCorrelationId,
} from "./baseQueryWithAuth.errors";
import {
    handleAiInsightsEntitlementStub,
    handleDemoAccessStub,
} from "./baseQueryWithAuth.stubs";

const logger = FrontendLoggerFactory.getLogger().withComponent(
    "store/api/baseQueryWithAuth",
);

const isRetryableHttpStatus = (status: number): boolean => {
    // Retry only when we have a reasonable chance of succeeding on a subsequent attempt.
    // 4xx errors generally indicate a client/input/auth issue and should not be retried.
    if (status === 408) return true; // Request Timeout
    if (status === 429) return true; // Rate limited
    return status >= 500;
};

interface BaseQueryArgs {
    url: string;
    method?: AxiosRequestConfig["method"];
    data?: AxiosRequestConfig["data"];
    params?: AxiosRequestConfig["params"];
}

const getAuthHeaders = async (args: {
    correlationId: string;
}): Promise<Record<string, string>> => {
    const token = await memoizedAuth.getIdToken();

    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        [CORRELATION_ID_HEADER]: args.correlationId,
    };

    if (isPreviewEnvironment()) {
        const previewEmailOverride = getStoredPreviewEmailOverride();
        if (previewEmailOverride) {
            headers[PREVIEW_EMAIL_OVERRIDE_HEADER] = previewEmailOverride;
        }
    }

    if (token) {
        headers.Authorization = `Bearer ${token}`;
    } else {
        logger.warn("API request proceeding without Firebase auth token", {
            operation: "api_request_missing_token",
            correlationId: args.correlationId,
        });
    }

    return headers;
};

export const baseQueryWithAuth: BaseQueryFn<
    BaseQueryArgs,
    unknown,
    ApiError
> = async (
    { url, method = "GET", data, params },
    { signal },
) => {
    const normalizedUrl = `/${url.replace(/^\/+/, "").replace(/\/+$/, "")}`;
    const normalizedMethod = (method ?? "GET").toString().toUpperCase();
    const normalizedParams = (params ?? {}) as Record<string, unknown>;

    const aiInsightsStubResult = await handleAiInsightsEntitlementStub({
        normalizedMethod,
        normalizedUrl,
        normalizedParams,
        data,
    });
    if (aiInsightsStubResult) {
        return aiInsightsStubResult;
    }

    const demoAccessStubResult = await handleDemoAccessStub({
        normalizedMethod,
        normalizedUrl,
        normalizedParams,
        data,
    });
    if (demoAccessStubResult) {
        return demoAccessStubResult;
    }

    const fullUrl = `${import.meta.env.VITE_API_URL}${url}`;
    const requestStartTime = Date.now();
    const correlationId = generateCorrelationId();
    const requestLogger = logger.withCorrelationId(correlationId);
    const endpoint = normalizedUrl;

    try {
        const headers = await getAuthHeaders({ correlationId });

        logOperationStart(requestLogger, {
            endpoint,
            method: normalizedMethod,
            correlationId,
        });

        const result = await axios(fullUrl, {
            method,
            data,
            params,
            signal,
            headers,
            timeout: 60_000, // Some admin endpoints can be slow; keep a finite bound.
        });

        const requestDuration = Date.now() - requestStartTime;
        const responseCorrelationId = normalizeCorrelationId(
            result.headers?.[CORRELATION_ID_HEADER],
        );
        const resolvedCorrelationId = responseCorrelationId ?? correlationId;

        logOperationSuccess(requestLogger, {
            endpoint,
            method: normalizedMethod,
            status: result.status,
            durationMs: requestDuration,
            correlationId: resolvedCorrelationId,
        });

        return { data: result.data };
    } catch (axiosError) {
        const err = axiosError as AxiosError;
        const requestDuration = Date.now() - requestStartTime;
        const safeFallbackMessage = "Request failed";
        const normalizedError = normalizeApiError({
            axiosError: err,
            fallbackMessage: safeFallbackMessage,
        });
        const responseCorrelationId = normalizeCorrelationId(
            err.response?.headers?.[CORRELATION_ID_HEADER],
        );
        const resolvedCorrelationId = responseCorrelationId ?? correlationId;
        const safeError = new Error(normalizedError.message);
        const errorCode = err.code;
        const errorName = err.name || "AxiosError";
        const errorStack = err.stack;
        safeError.name = errorName;
        safeError.stack = errorStack;
        const statusCode = normalizedError.status;

        if (errorCode === "ERR_CANCELED" || errorName === "CanceledError") {
            logOperationAbort(requestLogger, {
                endpoint,
                method: normalizedMethod,
                durationMs: requestDuration,
                correlationId: resolvedCorrelationId,
                extraMeta: {
                    reason: "REQUEST_ABORTED",
                    ...(typeof navigator !== "undefined"
                        ? { offline: !navigator.onLine }
                        : {}),
                },
            });

            return {
                error: {
                    // 499 is a commonly-used "client closed request" status.
                    status: 499,
                    data: null,
                    message: "Request aborted",
                },
            };
        }

        logOperationFail(requestLogger, {
            endpoint,
            method: normalizedMethod,
            error: safeError,
            status: statusCode,
            durationMs: requestDuration,
            correlationId: resolvedCorrelationId,
            extraMeta: {
                ...(typeof navigator !== "undefined"
                    ? { offline: !navigator.onLine }
                    : {}),
                ...(errorCode ? { axiosCode: errorCode } : {}),
                isAxiosError: axios.isAxiosError(axiosError),
            },
        });

        return {
            error: normalizedError,
        };
    }
};

// Enhanced base query with retry and token refresh
export const enhancedBaseQuery = retry(
    async (args, api, extraOptions) => {
        let result = await baseQueryWithAuth(args, api, extraOptions);

        // Handle 401 errors with token refresh
        if (result.error?.status === 401) {
            try {
                // Force token refresh
                const user = memoizedAuth.getCurrentUser();
                if (user) {
                    await user.getIdToken(true);
                    // Retry the original request
                    result = await baseQueryWithAuth(args, api, extraOptions);
                } else {
                    logger.warn("Token refresh skipped because no authenticated user is available", {
                        operation: "token_refresh_no_user",
                    });
                }
            } catch {
                logger.warn("Token refresh failed; redirecting to login", {
                    operation: "token_refresh_failed",
                });
                // Token refresh failed, redirect to login
                window.location.href = "/login";
            }
        }

        // Stop retries early for non-retryable HTTP statuses.
        if (result.error) {
            const status = result.error.status;

            if (
                status === 401 ||
                status === 499 ||
                (typeof status === "number" && !isRetryableHttpStatus(status))
            ) {
                return retry.fail(result.error);
            }
        }

        return result;
    },
    {
        maxRetries: 3,
        backoff: async attempt => {
            // Keep retries bounded for operator routes so permission-gated workspaces
            // fail fast and can surface actionable recovery UI.
            const delay = 1000 * attempt;
            logger.debug("API retry backoff", {
                operation: "api_retry_backoff",
                attempt,
                delayMs: delay,
            });
            await new Promise(resolve => setTimeout(resolve, delay));
        },
    },
);

export { normalizeApiError };
